import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FolderKanban, CheckSquare, Target, FileText, Wallet, UsersRound, Settings, LogOut, Menu, X, Compass, Package, GanttChart, Loader, CalendarDays, Newspaper, Repeat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/shared";
import { ClientBrandProvider } from "@/components/ClientBrand";
import { BrandSwitcher } from "@/components/BrandSwitcher";

const NAV = {
  admin: [
    ["Dashboard", "/dashboard", LayoutDashboard], ["My Work", "/my-work", Compass], ["Clients", "/clients", Users], ["Projects", "/projects", FolderKanban],
    ["Tasks", "/tasks", CheckSquare], ["Goals", "/goals", Target], ["Proposals", "/proposals", FileText],
    ["Accounts", "/accounts", Wallet], ["Team", "/team", UsersRound], ["Settings", "/settings", Settings],
  ],
  employee: [
    ["My Work", "/my-work", Compass], ["Clients", "/clients", Users], ["Projects", "/projects", FolderKanban], ["Tasks", "/tasks", CheckSquare],
    ["Goals", "/goals", Target], ["Proposals", "/proposals", FileText], ["Team", "/team", UsersRound],
  ],
  client: [["Home", "/portal", LayoutDashboard], ["Products", "/portal/products", Package], ["Orders", "/portal/orders", FolderKanban], ["Timeline", "/portal/timeline", GanttChart], ["In process", "/portal/in-process", Loader],
    ["Content", "/portal/content", CalendarDays], ["Reports", "/portal/reports", Newspaper], ["Retainer", "/portal/retainer", Repeat], ["Invoices & Quotes", "/accounts", Wallet]],
};

export default function Layout({ children }) {
  const { user, logout, brand } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV[user.role] || [];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <ClientBrandProvider><div className="min-h-screen flex bg-[#F8FAFC]">
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-[#0F172A] text-white flex flex-col transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-5 pt-5 pb-4 border-b border-white/10">
          <div className="flex items-start justify-between gap-2">
            <img src={brand.logo_light} alt={brand.agency_name} className="w-full max-w-[180px] h-auto object-contain" data-testid="sidebar-logo" />
            <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} data-testid="sidebar-close-btn"><X size={18} /></button>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-3">{user.role} workspace</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {items.map(([name, to, Icon]) => (
            <NavLink key={to} to={to} end={to === "/portal"} onClick={() => setOpen(false)} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid={`nav-${name.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon size={17} /> {name}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar name={user.name} src={user.picture} size={32} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user.name}</div>
              <div className="text-xs text-slate-400 truncate">{user.email}</div>
            </div>
            <button onClick={doLogout} title="Sign out" className="text-slate-400 hover:text-white transition-colors" data-testid="logout-btn"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-slate-950/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 lg:pl-60 min-w-0">
        <header className={`sticky top-0 z-20 h-16 flex items-center gap-3 px-4 sm:px-8 bg-white/90 backdrop-blur-xl border-b border-slate-200/80 ${user.role === "client" ? "lg:hidden" : ""}`} data-testid="workspace-topbar">
          <button onClick={() => setOpen(true)} className="text-slate-700 lg:hidden" aria-label="Open navigation" data-testid="sidebar-open-btn"><Menu size={20} /></button>
          {user.role === "client" ? <img src={brand.logo_dark} alt={brand.agency_name} className="h-7 w-auto" /> : <><BrandSwitcher /><span className="hidden md:block ml-auto text-xs text-slate-400" data-testid="brand-switcher-topbar-help">Your clients. One workspace.</span></>}
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1400px]">{children}</main>
      </div>
    </div></ClientBrandProvider>
  );
}
